import { ObjectListRequestType } from '../ObjectListResponseTypes'
import { ObjectListType } from '../ObjectListTypes'
import { TableFilter } from './TicketFilterType'

export enum TicketStatusId {
  InProgress = 1,
  OnHold = 2,
  Completed = 3,
  FollowUp = 4,
  ToDo = 21,
  CustomerReplyNeeded = 22,
}

export enum TicketPriority {
  Urgent = 1,
  High = 2,
  Medium = 4,
  Low = 5,
}

/**
 * Logical Groupings for Filters and Colors
 */
export const TicketStatusGroup = {
  // Green / Done
  Closed: [TicketStatusId.Completed],

  // Yellow / Waiting
  Pending: [TicketStatusId.OnHold, TicketStatusId.CustomerReplyNeeded, TicketStatusId.FollowUp],

  // Red / Action Required
  Open: [TicketStatusId.InProgress, TicketStatusId.ToDo],
} as const

/**
 * Helper to get the TRANSLATION KEY for a status.
 */
export const getStatusLabelKey = (statusKey: keyof typeof TicketStatusId): string => {
  // Just return the key string
  return `filter.status.${statusKey}`
}

/**
 * Helper to get the color based on the Status ID.
 * Replaces your old checkStatusColor function.
 */
export const getStatusColor = (statusId: number): string => {
  // Check 'Closed' Group (Green)
  if ((TicketStatusGroup.Closed as readonly number[]).includes(statusId)) {
    return '#2DF1D0'
  }

  // Check 'Pending' Group (Yellow)
  if ((TicketStatusGroup.Pending as readonly number[]).includes(statusId)) {
    return '#FFDE07'
  }

  // Default / Open Group (Red)
  return '#FF5050'
}

export interface TicketList extends ObjectListType {
  tickets: Ticket[]
}
export interface Ticket {
  ticketId: number
  description: string
  date: string
  departmentId: number
  department: string
  departmentsub: string
  statusId: number
  status: string
  history: Array<TicketHistory>
  ticketLinks: Array<TicketLink>
  priority: string
  timeOpened: string
  resolution: string
  closed_on: string
  is_archived: boolean
  createdBy: string
  zabbix_link: string
}
export interface TicketLink {
  objectName: string
  objectId: number
}
export interface TicketListRequest extends ObjectListRequestType {
  statusid?: number
  filters?: TableFilter
  ticketId?: number
}

export interface ticketFilter {
  subject?: string
  content?: string
  ticketId?: number
  priority?: string
}

export interface TicketHistoryList extends ObjectListType {
  history: TicketHistory[]
}
export interface TicketHistory {
  historyId: number
  date: string
  inputType: string
  actionType: string
  content: string
  employee: string
  createdBy: string
  attachments: Array<TicketAttachment>
}
export interface TicketAttachment {
  fileName: string
  fileType: string
  fileDate: string
  fileUrl: string
  fileId: number
}

export const isTicketListType = (ticketlist: any): ticketlist is TicketList => {
  if (ticketlist === undefined) {
    return false
  }
  return 'tickets' in ticketlist
}

export const isTicket = (ticket: any): ticket is Array<Ticket> => {
  if (ticket === undefined) {
    return false
  }
  if (Array.isArray(ticket)) {
    return 'ticketId' in ticket[0]
  }
  return 'ticketId' in ticket
}
export const isTicketHistory = (history: any): history is Array<TicketHistory> => {
  if (history === undefined) {
    return false
  }
  if (Array.isArray(history)) {
    return 'historyId' in history[0] && 'inputType' in history[0]
  }
  return 'historyId' in history && 'inputType' in history
}

export const isTicketHistoryList = (request: any): request is TicketHistoryList => {
  if (request === undefined) {
    return false
  }
  return 'history' in request
}
export const isTicketListRequest = (request: any): request is TicketListRequest => {
  if (request === undefined) {
    return false
  }
  return 'statusid' in request || 'filters' in request
}
